<template>
    <modals-side-generic
    :headerColor="headerColor"
    :title="title"
    :subTitle="subTitle"
    :subTitleIcon="subTitleIcon"
    :icon="icon"
    :showFooter="showFooter && !showLoader"
    :buttonsTopData="buttonsTopData"
    :buttonsFooterData="buttonsFooterData"
    :sizeClass="sizeClass"
    :triggerClose="triggerClose"
    @hideMe="emit('hideMe')">
        <div v-for="(lineGroup, index) in parsedMdwInfo" :key="`lineGroup${lineGroup.id}`" class="group-wrapper">
            <divider-generic
            dividerSize="medium"
            :icon="lineGroup.icon"
            :title="lineGroup.label"
            :applyBorder="index !== 0"/>
            <!-- Text Block for each line -->
            <text-block-generic
            v-for="line in lineGroup.lines"
            :key="`lineGroupLine${line,id}`"
            :params="line"
            :icon="line.icon"
            :mask="line.mask"
            :colorIconPreset="line.colorIconPreset"
            applyEllipsis/>
        </div>
        <!-- Bottom content -->
        <slot v-if="showBottomContent && !showLoader && !showEmptyErrorState" name="bottomContent"></slot>
        <!-- Loader -->
        <loader-generic v-if="showLoader" fullSize/>
        <!-- Feedback Toast -->
        <toast-generic
        v-if="showToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showToast = false"/>
        <!-- Error State -->
        <empty-state-error v-if="showEmptyErrorState" :message="errorStateMessage"/>
    </modals-side-generic>
</template>

<script setup>
import ModalsSideGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'

/* Aux */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { parseLineGroups } from '@/assets/scripts/parsers/genericDetail'
/* Store */
import store from '@/store/store'/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe'])

/* Props
@headerColor {String} - color for header
@title {String} - text to display in our title
@subTitle {String} - text to display under our title
@icon: {String} - iconName+path(if needed) to display
@buttonsTopData {Object} -
@showFooter {Boolean} - prop to control display of footer
@buttonsFooterData {Array} - array of buttons to display on detail footer
@mdwLineGroupsData {Array} - Direct info from mdw, if detail is simply display of this info, our detail will parse and display it
    or
@detailRequestAction {String} - action that we will be calling for this detail
@requestPayload {Object} - payload for that action api
@errorStateMessage {String} - text to display on error state when api request turns into an error
@showBottomContent {Boolean} - to show bottom content
@forceNegativeAmount { Boolean } - to force negative amount icon and color
@statusIconSvgName { String } - name of the line status icon to display
@statusIconColor { String } - color of the line status icon to display
@statusDescription {String} - description from list to replace the one provided by mdw (is used for example in approvals)
@sizeClass {String} - possible values [medium(560), big(640), giant(900)], if not passed we will apply standard with 450
@triggerClose {Boolean} - prop to trigger close of filter modal so animation happens before our modal disapears
*/
const props = defineProps({
    headerColor: String,
    title: String,
	subTitle: String,
    icon: String,
    buttonsTopData: Object,
    showFooter: Boolean,
    buttonsFooterData: Array,
    mdwLineGroupsData: Array,
    detailRequestAction: String,
    requestPayload: Object,
    errorStateMessage: String,
    showBottomContent: Boolean,
    forceNegativeAmount: Boolean,
    statusIconSvgName: String,
    statusIconColor: String,
    statusDescription: String,
    sizeClass: String,
    triggerClose: Boolean
})

const showLoader = ref(!props.mdwLineGroupsData)
const showEmptyErrorState = ref(false)
const responseLineGroupsData = ref(null)
const { getMobileAppState } = mapGetters()

const parsedMdwInfo = computed(() => {
    /* If we receive info from parent, no request for detail is needed */
    if (props.mdwLineGroupsData) {
        return parseLineGroups(props.mdwLineGroupsData, props.statusIconSvgName, props.statusIconColor, props.forceNegativeAmount, props.statusDescription)
    }
    /* We don't wont to show data if loader is being showed */
    if (showLoader.value || showEmptyErrorState.value || !responseLineGroupsData.value) return []
    /* if we have the request done, and no loader is being showed, we will return our responsed parsed */
    return parseLineGroups(responseLineGroupsData.value, props.statusIconSvgName, props.statusIconColor, props.forceNegativeAmount, props.statusDescription)
})
/*************************************************
*                                                *
*                  Request Logic              *
*                                                *
*************************************************/
const requestHandler = async () => {
    showLoader.value = true

    const payload = { ...props.requestPayload }

    try {
        const res = await store.dispatch(props.detailRequestAction, payload)
        responseLineGroupsData.value = res.data.data.lineGroups
        showLoader.value = false
    } catch (error) {
        showLoader.value = false
        showToastHandler(error.response.data, requestHandler)
    }
}

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const showToastHandler = (data, retryFn) => {
    feedbackToastData.value = errorObjectConstructor(data, retryFn)

    showEmptyErrorState.value = true
    showToast.value = true
}

if (props.detailRequestAction) requestHandler()
</script>
